import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { AppDispatch } from 'app/store';

import { selectUser } from 'features/auth/authSelectors';
import { setDictionariesStatus } from 'features/dictionaries/dictionariesSlice';
import { fetchDictionariesBatch } from 'features/dictionaries/dictionariesThunks';
import { selectAppLocale } from 'features/locale/localeSelectors';

import useErrorCatch from 'hooks/useErrorCatch';

import { Dictionaries, RequestStatus } from 'constants/enums';

const useLoadDictionaries = () => {
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();
  const user = useSelector(selectUser);
  const locale = useSelector(selectAppLocale);
  const catchError = useErrorCatch();

  const loadDictionaries = useCallback(async () => {
    const dictionaries: Array<Dictionaries> = [
      Dictionaries.JobseekerStage,
      Dictionaries.Education,
      Dictionaries.Study,
      Dictionaries.JobShift,
      Dictionaries.Language,
      Dictionaries.ImmigrationRoutes,
      Dictionaries.LanguageLevel,
      Dictionaries.SourcePlatform,
      Dictionaries.JobseekerStageTag,
      Dictionaries.ApplicationStageTag,
      Dictionaries.JobFeeStructure,
      Dictionaries.JobStatus,
      Dictionaries.Industry,
      Dictionaries.ClientCountry,
      Dictionaries.Skillset,
      Dictionaries.ApplicantStatus,
      Dictionaries.ClientPrivatePublic,
      Dictionaries.ClientStatus,
      Dictionaries.ClientDomesticOver,
      Dictionaries.ClientYearsInBus,
      Dictionaries.JobType,
      Dictionaries.PayrollRates,
      Dictionaries.JobseekerStageFlow,
      Dictionaries.ClientTier,
      Dictionaries.ApplicationStageClient,
      Dictionaries.ApplicationStageClientTag,
      Dictionaries.ApplicationClientStageFlow,
      Dictionaries.JobClientStatusFlow,
      Dictionaries.JobseekerAvailabilityStatus,
      Dictionaries.FeedbackCategory
    ];

    dispatch(setDictionariesStatus(RequestStatus.Loading));

    try {
      await dispatch(
        fetchDictionariesBatch({
          dicts: dictionaries,
          locale
        })
      ).unwrap();

      dispatch(setDictionariesStatus(RequestStatus.Succeeded));
    } catch (error) {
      dispatch(setDictionariesStatus(RequestStatus.Failed));
      catchError({
        error,
        default: {
          message: t('error.message.fetchDic')
        }
      });
    } finally {
      dispatch(setDictionariesStatus(RequestStatus.Idle));
    }
  }, [dispatch, catchError, locale]);

  useEffect(() => {
    if (!user.id || Object.keys(user).length === 0) {
      return;
    }

    loadDictionaries();
  }, [loadDictionaries, user.id]);
};

export default useLoadDictionaries;
